// Admin API Service for Public Schema Operations - JWT Based
class AdminApi {
  constructor() {
    this.baseUrl = '/admin-ui'
    const storedToken = localStorage.getItem('admin_token')
    this.token = storedToken || null
  }

  // Get authorization headers with JWT token
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    }
    
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }
    
    return headers
  }

  // Store JWT token
  setToken(token) {
    this.token = token
    localStorage.setItem('admin_token', token)
    const stored = localStorage.getItem('admin_token')
  }

  // Clear JWT token
  clearToken() {
    this.token = null
    localStorage.removeItem('admin_token')
  }

  async login(credentials) {
    // Use unified auth for login
    const unifiedAuth = await import('./unifiedAuth.js')
    const result = await unifiedAuth.default.login(credentials)
    
    if (result.success) {
      // Update our token to match the admin token from unified auth
      if (result.admin?.success) {
        this.setToken(result.admin.token)
      } else if (result.scheduler?.success) {
        // Fallback to scheduler token if admin login failed but scheduler succeeded
        this.setToken(result.scheduler.token)
      }
      
      return {
        success: true,
        token: this.token,
        capabilities: unifiedAuth.default.getCapabilities(),
        systems: {
          scheduler: result.scheduler?.success || false,
          admin: result.admin?.success || false
        }
      }
    } else {
      throw new Error(`Login failed: ${result.errors.join(', ')}`)
    }
  }

  async logout() {
    try {
      // For JWT, logout is just clearing the token client-side
      this.clearToken()
      return { success: true, message: 'Logged out successfully' }
    } catch (error) {
      console.error('Logout error:', error)
      throw error
    }
  }

  async checkSession() {
    try {
      if (!this.token) {
        return false
      }
      // Test the token by making a simple API call
      const response = await fetch(`${this.baseUrl}/api/dashboard/`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })
      
      if (!response.ok) {
        const errorText = await response.text()
        // Token is invalid, clear it
        this.clearToken()
        return false
      }
      
      return true
    } catch (error) {
      this.clearToken()
      return false
    }
  }

  async getDashboardData() {
    const response = await fetch(`${this.baseUrl}/api/dashboard/`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken()
        window.location.href = '/saas/login'
        return
      }
      throw new Error('Failed to load dashboard data')
    }
    
    return await response.json()
  }

  async getCompanies() {
    const response = await fetch(`${this.baseUrl}/api/companies/`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken()
        window.location.href = '/saas/login'
        return
      }
      throw new Error('Failed to load companies')
    }
    
    return await response.json()
  }

  async createCompany(companyData) {
    const response = await fetch(`${this.baseUrl}/api/companies/`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(companyData)
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken()
        window.location.href = '/saas/login'
        return
      }
      const error = await response.json().catch(() => ({ detail: 'Failed to create company' }))
      throw new Error(error.detail || 'Failed to create company')
    }
    
    return await response.json()
  }

  async updateCompany(id, companyData) {
    const response = await fetch(`${this.baseUrl}/api/companies/${id}/`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(companyData)
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken()
        window.location.href = '/saas/login'
        return
      }
      const error = await response.json().catch(() => ({ detail: 'Failed to update company' }))
      throw new Error(error.detail || 'Failed to update company')
    }
    
    return await response.json()
  }

  async deleteCompany(id) {
    const response = await fetch(`${this.baseUrl}/api/companies/${id}/`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken()
        window.location.href = '/saas/login'
        return
      }
      const error = await response.json().catch(() => ({ detail: 'Failed to delete company' }))
      throw new Error(error.detail || 'Failed to delete company')
    }
    
    return { success: true }
  }

  async getInvoices() {
    const response = await fetch(`${this.baseUrl}/api/invoices/`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken()
        window.location.href = '/saas/login'
        return
      }
      throw new Error('Failed to load invoices')
    }
    
    return await response.json()
  }

  async createInvoice(invoiceData) {
    const response = await fetch(`${this.baseUrl}/api/invoices/`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(invoiceData)
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken()
        window.location.href = '/saas/login'
        return
      }
      const error = await response.json().catch(() => ({ detail: 'Failed to create invoice' }))
      throw new Error(error.detail || 'Failed to create invoice')
    }
    
    return await response.json()
  }

  async updateInvoice(id, invoiceData) {
    const response = await fetch(`${this.baseUrl}/api/invoices/${id}/`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(invoiceData)
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken()
        window.location.href = '/saas/login'
        return
      }
      const error = await response.json().catch(() => ({ detail: 'Failed to update invoice' }))
      throw new Error(error.detail || 'Failed to update invoice')
    }
    
    return await response.json()
  }

  async deleteInvoice(id) {
    const response = await fetch(`${this.baseUrl}/api/invoices/${id}/`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken()
        window.location.href = '/saas/login'
        return
      }
      const error = await response.json().catch(() => ({ detail: 'Failed to delete invoice' }))
      throw new Error(error.detail || 'Failed to delete invoice')
    }
    
    return { success: true }
  }

  clearSession() {
    // Legacy method for compatibility - just clear token
    this.clearToken()
  }
}

export const adminApi = new AdminApi()
